import { useEffect, useState } from 'react';
import { useAuditLog } from './useAuditLog';
import { toast } from './use-toast';

export interface SecurityAlert {
  id: string;
  type: 'bulk_delete' | 'high_value' | 'rapid_requests' | 'suspicious_activity';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  timestamp: Date;
  metadata?: Record<string, any>;
}

/**
 * Hook para detectar e alertar sobre ações suspeitas
 */
export const useSecurityAlerts = () => {
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const { getAuditLogs } = useAuditLog();
  
  /**
   * Adiciona um alerta
   */
  const addAlert = (alert: Omit<SecurityAlert, 'id' | 'timestamp'>) => {
    const newAlert: SecurityAlert = {
      ...alert,
      id: crypto.randomUUID(),
      timestamp: new Date(),
    };
    
    setAlerts(prev => [newAlert, ...prev].slice(0, 100)); // Manter últimos 100
    
    // Mostrar toast para alertas críticos
    if (alert.severity === 'critical' || alert.severity === 'high') {
      toast({
        variant: 'destructive',
        title: '⚠️ Alerta de Segurança',
        description: alert.message,
        duration: 5000,
      });
    }
  };
  
  /**
   * Detecta exclusões em massa (mais de 10 transações de uma vez)
   */
  const detectBulkDelete = (count: number) => {
    if (count > 10) {
      addAlert({
        type: 'bulk_delete',
        severity: 'medium',
        message: `${count} transações foram excluídas de uma vez. Isso é normal?`,
        metadata: { deletedCount: count }
      });
    }
  };
  
  /**
   * Detecta alterações de valor alto (> R$ 10.000)
   */
  const detectHighValueTransaction = (value: number, description: string) => {
    if (value > 10000) {
      addAlert({
        type: 'high_value',
        severity: 'medium',
        message: `Transação de alto valor detectada: R$ ${value.toFixed(2)} - ${description}`,
        metadata: { value, description }
      });
    }
  };
  
  /**
   * Detecta múltiplas requisições rápidas (possível ataque)
   */
  const detectRapidRequests = (requestCount: number, timeWindow: number) => {
    const threshold = 50; // 50 requisições em timeWindow ms
    if (requestCount > threshold) {
      addAlert({
        type: 'rapid_requests',
        severity: 'high',
        message: `${requestCount} requisições em ${timeWindow}ms. Possível ataque detectado.`,
        metadata: { requestCount, timeWindow }
      });
    }
  };
  
  /**
   * Analisa logs de auditoria em busca de padrões suspeitos
   */
  const analyzeAuditLogs = async () => {
    try {
      const logs = await getAuditLogs(50);
      
      // Detectar múltiplas exclusões em curto período
      const recentDeletes = logs.filter(
        (log: any) => log.action === 'BULK_DELETE' || log.action === 'DELETE'
      );
      
      if (recentDeletes.length > 5) {
        addAlert({
          type: 'suspicious_activity',
          severity: 'medium',
          message: `${recentDeletes.length} operações de exclusão nos últimos registros`,
          metadata: { deleteCount: recentDeletes.length }
        });
      }
      
      // Detectar mudanças frequentes na mesma tabela
      const tableCounts = logs.reduce((acc, log: any) => {
        acc[log.table_name] = (acc[log.table_name] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      Object.entries(tableCounts).forEach(([table, count]: [string, number]) => {
        if (count > 20) {
          addAlert({
            type: 'suspicious_activity',
            severity: 'low',
            message: `Muitas operações na tabela ${table}: ${count} ações recentes`,
            metadata: { table, actionCount: count }
          });
        }
      });
      
    } catch (error) {
      console.error('Error analyzing audit logs:', error);
    }
  };
  
  /**
   * Limpa alertas antigos (> 7 dias)
   */
  const clearOldAlerts = () => {
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    setAlerts(prev => prev.filter(alert => alert.timestamp.getTime() > sevenDaysAgo));
  };
  
  /**
   * Remove um alerta específico
   */
  const dismissAlert = (alertId: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  };
  
  /**
   * Limpa todos os alertas
   */
  const clearAllAlerts = () => {
    setAlerts([]);
  };
  
  /**
   * Obtém alertas por severidade
   */
  const getAlertsBySeverity = (severity: SecurityAlert['severity']) => {
    return alerts.filter(alert => alert.severity === severity);
  };
  
  // Análise periódica (a cada 30 minutos, pausa quando aba oculta)
  // OTIMIZAÇÃO EGRESS: reduzido de 5 min → 30 min + pausa em background
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (interval) return;
      analyzeAuditLogs();
      interval = setInterval(analyzeAuditLogs, 30 * 60 * 1000);
    };
    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);
  
  // Limpeza periódica de alertas antigos (diária)
  useEffect(() => {
    clearOldAlerts();
    const interval = setInterval(clearOldAlerts, 24 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  return {
    alerts,
    addAlert,
    detectBulkDelete,
    detectHighValueTransaction,
    detectRapidRequests,
    analyzeAuditLogs,
    dismissAlert,
    clearAllAlerts,
    getAlertsBySeverity,
    criticalAlerts: getAlertsBySeverity('critical'),
    highAlerts: getAlertsBySeverity('high'),
  };
};
